import { ROUTES } from "src/app/constants";
import type { LobKey } from "src/jsonForms/preScreen/types";
import type { ClaimType, SearchConfig } from "./types";
import { WC_SEARCH_CONFIG } from "./configs/wc";
import { CARGO_SEARCH_CONFIG } from "./configs/cargo";
import { IPL_SEARCH_CONFIG } from "./configs/ipl";

export interface SearchConfigRegistryEntry {
  readonly routePath: string;
  /**
   * The `lineOfBusiness` sent to `POST /resolveSearchConfig`, which the service
   * turns into the stored document's key, `{lineOfBusiness}-search-config.json`.
   *
   * Declared here rather than read off `searchConfig.claimType`, because the page
   * needs it to fetch the document and so cannot take it from the document. The
   * registry tests hold the two equal, so the address and the seed cannot drift.
   */
  readonly lineOfBusiness: ClaimType;
  /**
   * The standalone route presenting this line of business in
   * my-submitted-claims mode, where one exists. Workers Compensation is the
   * only one today.
   *
   * Declared because it cannot be derived: it carries no `/claims-history`
   * suffix, so `claimsHistorySegment` cannot see it. Registry-level rather than
   * part of the `SearchConfig` document, since it is routing for this app and
   * not something a backend-served configuration would carry.
   */
  readonly mySubmittedClaimsRoutePath?: string;
  /**
   * Not what the config-driven page renders — that is the document served by
   * `ResolveSearchConfig`. This is the reviewed source each stage's document was
   * seeded from, and it still has live readers that never fetch:
   *
   * - the three legacy claims history components, which take their search bar
   *   fields from it. They are what renders with the flag off, as in prod, and
   *   whenever the flag lookup fails, so a `searchableFields` change here ships
   *   to those users with the next UI deploy;
   * - the shell's tab set, on both sides of the flag;
   * - the retrieval metric's publisher name, when no document was reached.
   *
   * Change it and the stored documents together. It is deleted with the legacy
   * components, once production runs on the config-driven page.
   */
  readonly searchConfig: SearchConfig;
}

/**
 * Partial rather than total so a LobKey without a claims history — `atoz`
 * today — is representable without a placeholder entry. A total record would
 * not compile until an AtoZ entry existed, and AtoZ has no retrieval
 * operation to back one.
 */
export const SEARCH_CONFIG_REGISTRY: Partial<Record<LobKey, SearchConfigRegistryEntry>> = {
  wc: {
    routePath: ROUTES.CLAIMS_HISTORY,
    lineOfBusiness: "workers-compensation",
    mySubmittedClaimsRoutePath: ROUTES.MY_SUBMITTED_CLAIMS,
    searchConfig: WC_SEARCH_CONFIG,
  },
  cargo: {
    routePath: ROUTES.CARGO_CLAIMS_HISTORY,
    lineOfBusiness: "cargo",
    searchConfig: CARGO_SEARCH_CONFIG,
  },
  ipl: {
    routePath: ROUTES.IPL_CLAIMS_HISTORY,
    lineOfBusiness: "product-liability",
    searchConfig: IPL_SEARCH_CONFIG,
  },
};

const CLAIMS_HISTORY_SUFFIX = "/claims-history";

/**
 * The line-of-business segment of a claims history path: the segment
 * immediately preceding the suffix, lowercased, ignoring the fragment, the
 * query string, and trailing slashes. Null for a path without the suffix.
 *
 * Exported so the registry, the resolver, and their tests all read a path the
 * same way. Keying the map on the *first* segment while resolving on the one
 * preceding the suffix agreed only while every registered route was exactly two
 * segments; a deeper route would have been keyed under one segment and looked up
 * under another, and the config would have silently failed to resolve.
 */
export function claimsHistorySegment(rawPath: string): string | null {
  const pathOnly = rawPath.split("#")[0].split("?")[0].replace(/\/+$/, "");
  if (!pathOnly.toLowerCase().endsWith(CLAIMS_HISTORY_SUFFIX)) return null;
  const segment = pathOnly.slice(0, -CLAIMS_HISTORY_SUFFIX.length).split("/").pop();
  return segment ? segment.toLowerCase() : null;
}

/**
 * Segment to registered LobKey. Derived from the registry rather than declared
 * beside it so a route path and its segment cannot disagree.
 *
 * A registered path that is not a claims history path contributes no entry, and
 * so resolves to no line of business. That is a misconfigured registry rather
 * than a reachable state, and the registry tests are what catch it.
 */
const SEGMENT_TO_LOB: ReadonlyMap<string, LobKey> = new Map(
  Object.entries(SEARCH_CONFIG_REGISTRY).flatMap(([lob, entry]) => {
    const segment = claimsHistorySegment(entry!.routePath);
    return segment === null ? [] : [[segment, lob as LobKey] as [string, LobKey]];
  })
);

/**
 * Resolves a claims history path to its Line of Business. Returns null for a
 * path without the suffix or with an unregistered segment.
 *
 * Suffix-based, so it does not see a my-submitted-claims route. Prefer
 * `routeLineOfBusiness` unless you specifically mean "a claims history path".
 */
export function deriveLineOfBusiness(rawPath: string): LobKey | null {
  const segment = claimsHistorySegment(rawPath);
  return segment === null ? null : (SEGMENT_TO_LOB.get(segment) ?? null);
}

/** Comparable form of a path: no fragment, no query, no trailing slash, lowercased. */
function normalizePath(rawPath: string): string {
  return rawPath.split("#")[0].split("?")[0].replace(/\/+$/, "").toLowerCase();
}

/**
 * Exact my-submitted-claims route to LobKey, derived from the registry so a
 * declared route and the line of business it resolves to cannot disagree.
 */
const MY_SUBMITTED_CLAIMS_PATH_TO_LOB: ReadonlyMap<string, LobKey> = new Map(
  Object.entries(SEARCH_CONFIG_REGISTRY).flatMap(([lob, entry]) =>
    entry!.mySubmittedClaimsRoutePath === undefined
      ? []
      : [[normalizePath(entry!.mySubmittedClaimsRoutePath), lob as LobKey] as [string, LobKey]]
  )
);

/**
 * Resolves any route this page is mounted on to its Line of Business: a claims
 * history path by its suffix, a my-submitted-claims path by exact match.
 *
 * The one resolution, used by the route loader and by the configuration
 * resolver alike. They previously derived this separately, and diverged: the
 * loader mapped my-submitted-claims to `wc` while the resolver saw an
 * unregistered route, so that page authorized and then rendered a configuration
 * error instead of the user's claims. Any new claims history surface belongs in
 * the registry rather than in a second mapping beside a call site.
 */
export function routeLineOfBusiness(rawPath: string): LobKey | null {
  return (
    MY_SUBMITTED_CLAIMS_PATH_TO_LOB.get(normalizePath(rawPath)) ?? deriveLineOfBusiness(rawPath)
  );
}
